import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getClientDealsSummary, type ClientDealSummaryItem } from '../../api';
import { SALES_STAGE_META } from '../admin/crm/crmLabels';
import type { SalesStage } from '../../api';

type Props = {
  userId: number;
  onOpenDeal: (orderId: number) => void;
  hapticSelection: () => void;
  refreshKey?: number;
};

function dealChipLabel(d: ClientDealSummaryItem, showContext: boolean): string {
  const stage = d.sales_stage ? (SALES_STAGE_META[d.sales_stage as SalesStage]?.label ?? d.sales_stage) : '';
  const base = `${d.order_number}${stage ? ` · ${stage}` : ''} · ${d.item_count} поз.`;
  if (!showContext) return base;
  const ctx = (d.contact_name || d.delivery_address || '').trim();
  if (!ctx) return base;
  return `${base} · ${ctx.slice(0, 28)}${ctx.length > 28 ? '…' : ''}`;
}

export function AdminClientDealsStrip({ userId, onOpenDeal, hapticSelection, refreshKey = 0 }: Props) {
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState<ClientDealSummaryItem[]>([]);
  const [role, setRole] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClientDealsSummary(userId);
      setDeals(res.open_deals);
      setRole(res.role);
    } catch {
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1">
        <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-3)' }} />
        <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>Сделки…</span>
      </div>
    );
  }

  if (deals.length === 0) return null;

  const isPartner = role === 'partner';
  const primary = deals[0];

  if (deals.length === 1 && primary) {
    return (
      <div
        className="rounded-xl px-3 py-2.5 flex items-center justify-between gap-2"
        style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid rgba(99,179,237,0.28)' }}
      >
        <p className="text-[11px] font-semibold truncate min-w-0" style={{ color: 'var(--text-2)' }}>
          {dealChipLabel(primary, isPartner)}
        </p>
        <button
          type="button"
          className="press shrink-0 text-[10px] font-bold px-2.5 py-1.5 rounded-lg"
          style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED' }}
          onClick={() => {
            hapticSelection();
            onOpenDeal(primary.id);
          }}
        >
          Открыть
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>
        Открытые сделки ({deals.length})
      </p>
      <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-none">
        {deals.map((d) => (
          <button
            key={d.id}
            type="button"
            className="press shrink-0 text-[10px] font-semibold px-2.5 py-2 rounded-xl max-w-[200px] truncate"
            style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan)', border: '1px solid rgba(99,179,237,0.25)' }}
            onClick={() => {
              hapticSelection();
              onOpenDeal(d.id);
            }}
          >
            {dealChipLabel(d, isPartner)}
          </button>
        ))}
      </div>
    </div>
  );
}
